import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
} from "firebase/firestore";
import { VisualCalendar, CalendarSyncButton } from "@/components/calendar";
import { CompactCalendarWidget } from "@/components/dashboard/CompactCalendarWidget";
import { formatCurrency } from "@/config/company.config";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { LanguageSelector } from "@/components/ui/language-selector";
import {
  Users,
  Briefcase,
  Calendar,
  Video,
  FileText,
  DollarSign,
  Clock,
  CheckCircle,
  TrendingUp,
  MessageSquare,
  UserPlus,
  Link as LinkIcon,
  Eye,
  Search,
  Plus,
  Handshake,
} from "lucide-react";
import { OnboardingGuide } from '@/components/onboarding/OnboardingGuide';

// Types
interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  type: 'buyer' | 'seller' | 'broker';
  addedAt: any;
  activeMandates: number;
  totalPaid: number;
  status: 'active' | 'inactive' | 'completed';
  profileUrl?: string;
  calendarId?: string;
}

interface Mandate {
  id: string;
  clientId: string;
  clientName: string;
  dealId?: string;
  dealName?: string;
  type: 'legal' | 'tax' | 'financial' | 'immigration' | 'due_diligence' | 'other';
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'review' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  startDate: any;
  dueDate: any;
  completedDate?: any;
  hourlyRate: number;
  hoursWorked: number;
  estimatedHours: number;
  totalFee: number;
  currency: string;
  documents: MandateDocument[];
  milestones: Milestone[];
  calendarIntegration?: MergedCalendarInfo;
}

interface MandateDocument {
  id: string;
  name: string;
  type: string;
  url: string;
  uploadedAt: any;
  uploadedBy: string;
}

interface Milestone {
  id: string;
  title: string;
  description: string;
  dueDate: any;
  completed: boolean;
  completedDate?: any;
}

interface Partnership {
  id: string;
  partnerId: string;
  partnerName: string;
  partnerType: 'buyer' | 'seller' | 'broker' | 'lender' | 'consultant';
  partnerCompany?: string;
  specialty?: string;
  status: 'pending' | 'active' | 'inactive';
  startDate: any;
  sharedDeals: string[];
  revenue: number;
  meetings: number;
  calendarMerged: boolean;
  mergedCalendarUrl?: string;
}

interface MergedCalendarInfo {
  consultantId: string;
  clientId: string;
  mergedCalendarUrl: string;
  syncEnabled: boolean;
  lastSyncAt?: any;
}

interface Meeting {
  id: string;
  clientId: string;
  clientName: string;
  mandateId?: string;
  title: string;
  description?: string;
  scheduledAt: any;
  duration: number;
  meetingLink: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
  attendees: string[];
  recordingUrl?: string;
  transcriptUrl?: string;
  notes?: string;
}

// Mock data for when Firestore collections are empty
const mockClients: Client[] = [
  {
    id: "mock-client-1",
    name: "Jean-Pierre Martin",
    email: "jp.martin@example.com",
    phone: "+1 514-555-1234",
    company: "Martin Industries Inc.",
    type: "buyer",
    addedAt: new Date(),
    activeMandates: 2,
    totalPaid: 15000,
    status: "active",
  },
  {
    id: "mock-client-2",
    name: "Sophie Tremblay",
    email: "s.tremblay@example.com",
    phone: "+1 438-555-5678",
    company: "Tremblay & Associés",
    type: "seller",
    addedAt: new Date(),
    activeMandates: 1,
    totalPaid: 8500,
    status: "active",
  },
  {
    id: "mock-client-3",
    name: "Marc Dubois",
    email: "m.dubois@broker.com",
    phone: "+1 613-555-9012",
    company: "Dubois Brokerage",
    type: "broker",
    addedAt: new Date(),
    activeMandates: 3,
    totalPaid: 22000,
    status: "active",
  },
  {
    id: "mock-client-4",
    name: "Claire Beaumont",
    email: "c.beaumont@example.com",
    phone: "+1 416-555-3456",
    company: "Beaumont Holdings",
    type: "buyer",
    addedAt: new Date(),
    activeMandates: 0,
    totalPaid: 5000,
    status: "inactive",
  },
];

const mockMandates: Mandate[] = [
  {
    id: "mock-mandate-1",
    clientId: "mock-client-1",
    clientName: "Jean-Pierre Martin",
    dealId: "deal-123",
    dealName: "Acquisition Tech Solutions",
    type: "legal",
    title: "Due Diligence Juridique - Tech Solutions",
    description: "Analyse complète des contrats et obligations légales pour l'acquisition",
    status: "in_progress",
    priority: "high",
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
    hourlyRate: 250,
    hoursWorked: 45,
    estimatedHours: 80,
    totalFee: 20000,
    currency: "CAD",
    documents: [],
    milestones: [
      { id: "m1", title: "Revue initiale", description: "Revue des documents", dueDate: new Date(), completed: true },
      { id: "m2", title: "Analyse contrats", description: "Analyse approfondie", dueDate: new Date(), completed: true },
      { id: "m3", title: "Rapport final", description: "Livraison rapport", dueDate: new Date(), completed: false },
    ],
  },
  {
    id: "mock-mandate-2",
    clientId: "mock-client-2",
    clientName: "Sophie Tremblay",
    type: "tax",
    title: "Planification Fiscale - Vente d'Entreprise",
    description: "Optimisation fiscale pour la vente de l'entreprise familiale",
    status: "in_progress",
    priority: "medium",
    startDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000),
    hourlyRate: 300,
    hoursWorked: 20,
    estimatedHours: 50,
    totalFee: 15000,
    currency: "CAD",
    documents: [],
    milestones: [
      { id: "m1", title: "Analyse fiscale", description: "Évaluation situation", dueDate: new Date(), completed: true },
      { id: "m2", title: "Stratégie", description: "Plan d'optimisation", dueDate: new Date(), completed: false },
    ],
  },
  {
    id: "mock-mandate-3",
    clientId: "mock-client-3",
    clientName: "Marc Dubois",
    type: "financial",
    title: "Évaluation Financière - Portfolio",
    description: "Analyse financière du portfolio de 5 entreprises",
    status: "completed",
    priority: "high",
    startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    completedDate: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
    hourlyRate: 275,
    hoursWorked: 65,
    estimatedHours: 60,
    totalFee: 17875,
    currency: "CAD",
    documents: [],
    milestones: [
      { id: "m1", title: "Collecte données", description: "Rassembler documents", dueDate: new Date(), completed: true },
      { id: "m2", title: "Analyse", description: "Évaluation complète", dueDate: new Date(), completed: true },
      { id: "m3", title: "Livraison", description: "Rapport final", dueDate: new Date(), completed: true },
    ],
  },
  {
    id: "mock-mandate-4",
    clientId: "mock-client-1",
    clientName: "Jean-Pierre Martin",
    type: "immigration",
    title: "Visa Investisseur - Processus Complet",
    description: "Accompagnement pour obtention visa investisseur",
    status: "pending",
    priority: "urgent",
    startDate: new Date(),
    dueDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
    hourlyRate: 350,
    hoursWorked: 5,
    estimatedHours: 40,
    totalFee: 14000,
    currency: "CAD",
    documents: [],
    milestones: [],
  },
];

const mockPartnerships: Partnership[] = [
  {
    id: "mock-partnership-1",
    partnerId: "partner-1",
    partnerName: "Cabinet Lavoie & Fils",
    partnerType: "broker",
    partnerCompany: "Lavoie M&A Services",
    specialty: "Fusions & Acquisitions",
    status: "active",
    startDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000),
    sharedDeals: ["deal-123", "deal-456"],
    revenue: 35000,
    meetings: 12,
    calendarMerged: true,
    mergedCalendarUrl: "/calendar/merged/lavoie",
  },
  {
    id: "mock-partnership-2",
    partnerId: "partner-2",
    partnerName: "Groupe Financier Montréal",
    partnerType: "lender",
    partnerCompany: "GFM Capital",
    specialty: "Financement Acquisitions",
    status: "active",
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
    sharedDeals: ["deal-789"],
    revenue: 18500,
    meetings: 6,
    calendarMerged: false,
  },
  {
    id: "mock-partnership-3",
    partnerId: "partner-3",
    partnerName: "Notaires Leblanc",
    partnerType: "consultant",
    partnerCompany: "Leblanc Notariat",
    specialty: "Transactions Immobilières",
    status: "pending",
    startDate: new Date(),
    sharedDeals: [],
    revenue: 0,
    meetings: 2,
    calendarMerged: false,
  },
];

const mockMeetings: Meeting[] = [
  {
    id: "mock-meeting-1",
    clientId: "mock-client-1",
    clientName: "Jean-Pierre Martin",
    mandateId: "mock-mandate-1",
    title: "Revue Due Diligence - Point Hebdomadaire",
    description: "Discussion sur l'avancement de l'analyse juridique",
    scheduledAt: { toDate: () => new Date(Date.now() + 2 * 24 * 60 * 60 * 1000) } as any,
    duration: 60,
    meetingLink: "https://meet.example.com/abc123",
    status: "scheduled",
    attendees: ["jp.martin@example.com", "consultant@example.com"],
  },
  {
    id: "mock-meeting-2",
    clientId: "mock-client-2",
    clientName: "Sophie Tremblay",
    mandateId: "mock-mandate-2",
    title: "Présentation Stratégie Fiscale",
    scheduledAt: { toDate: () => new Date(Date.now() + 5 * 24 * 60 * 60 * 1000) } as any,
    duration: 90,
    meetingLink: "https://meet.example.com/def456",
    status: "scheduled",
    attendees: ["s.tremblay@example.com", "consultant@example.com"],
  },
  {
    id: "mock-meeting-3",
    clientId: "mock-client-3",
    clientName: "Marc Dubois",
    title: "Nouveau Mandat - Discussion Initiale",
    scheduledAt: { toDate: () => new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) } as any,
    duration: 45,
    meetingLink: "https://meet.example.com/ghi789",
    status: "scheduled",
    attendees: ["m.dubois@broker.com", "consultant@example.com"],
  },
];

const translations = {
  en: {
    title: "Consultant Dashboard",
    subtitle: "Manage your clients and mandates",
    newMandate: "New Mandate",
    totalClients: "Total Clients",
    activeMandates: "Active Mandates",
    completed: "Completed",
    hoursWorked: "Hours Worked",
    partnerships: "Partnerships",
    totalRevenue: "Total Revenue",
    pendingRevenue: "Pending Revenue",
    overview: "Overview",
    myClients: "My Clients",
    mandates: "Mandates",
    partnershipsTab: "Partnerships",
    calendar: "Calendar",
    documents: "Documents",
    messages: "Messages",
    activeMandatesTitle: "Active Mandates",
    currentlyInProgress: "Currently in progress",
    noActiveMandates: "No active mandates",
    dueDate: "Due Date",
    hours: "Hours",
    viewDetails: "View Details",
    upcomingMeetings: "Upcoming Meetings",
    nextScheduled: "Next scheduled appointments",
    noUpcomingMeetings: "No upcoming meetings",
    joinMeeting: "Join Meeting",
    recentPartnerships: "Recent Partnerships",
    activePartnerships: "Your active business partnerships",
    noActivePartnerships: "No active partnerships",
    type: "Type",
    sharedDeals: "Shared Deals",
    calendarLabel: "Calendar",
    merged: "Merged",
    notMerged: "Not Merged",
    viewCalendar: "View Calendar",
    clientsTitle: "My Clients",
    clientsSubtitle: "Buyers, sellers, and brokers you work with",
    addClient: "Add Client",
    searchClients: "Search clients...",
    allStatus: "All Status",
    active: "Active",
    inactive: "Inactive",
    noClientsYet: "No clients yet",
    addFirstClient: "Add Your First Client",
    totalPaid: "Total Paid",
    contact: "Contact",
    viewProfile: "View Profile",
    mandatesTitle: "Mandates",
    allMandates: "All your active and completed mandates",
    noMandatesYet: "No mandates yet",
    createFirstMandate: "Create Your First Mandate",
    inProgress: "In Progress",
    pending: "Pending",
    client: "Client",
    progress: "Progress",
    startDate: "Start Date",
    rate: "Rate",
    milestones: "Milestones",
    linkedPartners: "Linked Partners",
    calendarMergedWithClient: "Calendar Merged with Client",
    totalFee: "Total Fee",
    updateProgress: "Update Progress",
    calendarMeetings: "Calendar & Meetings",
    calendarSubtitle: "Your schedule and merged calendars with clients",
    calendarComingSoon: "Calendar view coming soon",
    upcomingMeetingsCount: "upcoming meetings",
    documentsTitle: "Documents",
    documentsSubtitle: "All mandate-related documents",
    noDocumentsYet: "No documents yet",
    messagesTitle: "Messages",
    messagesSubtitle: "Client communications",
    noMessagesYet: "No messages yet",
    myPartnerships: "My Partnerships",
    partnershipsSubtitle: "Business relationships and collaborations",
    noPartnershipsYet: "No partnerships yet",
    partnershipsHint: "You'll be added as a partner when clients select you for their deals",
    activePartnershipsTitle: "Active Partnerships",
    specialty: "Specialty",
    revenue: "Revenue",
    calendarIntegration: "Calendar Integration",
    message: "Message",
    pendingPartnerships: "Pending Partnerships",
    loading: "Loading dashboard...",
  },
  fr: {
    title: "Tableau de bord Consultant",
    subtitle: "Gérez vos clients et mandats",
    newMandate: "Nouveau Mandat",
    totalClients: "Total Clients",
    activeMandates: "Mandats Actifs",
    completed: "Complétés",
    hoursWorked: "Heures Travaillées",
    partnerships: "Partenariats",
    totalRevenue: "Revenus Totaux",
    pendingRevenue: "Revenus en Attente",
    overview: "Aperçu",
    myClients: "Mes Clients",
    mandates: "Mandats",
    partnershipsTab: "Partenariats",
    calendar: "Calendrier",
    documents: "Documents",
    messages: "Messages",
    activeMandatesTitle: "Mandats Actifs",
    currentlyInProgress: "Actuellement en cours",
    noActiveMandates: "Aucun mandat actif",
    dueDate: "Date limite",
    hours: "Heures",
    viewDetails: "Voir Détails",
    upcomingMeetings: "Réunions à Venir",
    nextScheduled: "Prochains rendez-vous planifiés",
    noUpcomingMeetings: "Aucune réunion à venir",
    joinMeeting: "Rejoindre",
    recentPartnerships: "Partenariats Récents",
    activePartnerships: "Vos partenariats commerciaux actifs",
    noActivePartnerships: "Aucun partenariat actif",
    type: "Type",
    sharedDeals: "Deals Partagés",
    calendarLabel: "Calendrier",
    merged: "Fusionné",
    notMerged: "Non Fusionné",
    viewCalendar: "Voir Calendrier",
    clientsTitle: "Mes Clients",
    clientsSubtitle: "Acheteurs, vendeurs et courtiers avec qui vous travaillez",
    addClient: "Ajouter Client",
    searchClients: "Rechercher clients...",
    allStatus: "Tous Statuts",
    active: "Actif",
    inactive: "Inactif",
    noClientsYet: "Aucun client pour le moment",
    addFirstClient: "Ajoutez Votre Premier Client",
    totalPaid: "Total Payé",
    contact: "Contact",
    viewProfile: "Voir Profil",
    mandatesTitle: "Mandats",
    allMandates: "Tous vos mandats actifs et complétés",
    noMandatesYet: "Aucun mandat pour le moment",
    createFirstMandate: "Créez Votre Premier Mandat",
    inProgress: "En Cours",
    pending: "En Attente",
    client: "Client",
    progress: "Progression",
    startDate: "Date Début",
    rate: "Taux",
    milestones: "Jalons",
    linkedPartners: "Partenaires Liés",
    calendarMergedWithClient: "Calendrier Fusionné avec Client",
    totalFee: "Frais Totaux",
    updateProgress: "Mettre à Jour",
    calendarMeetings: "Calendrier & Réunions",
    calendarSubtitle: "Votre emploi du temps et calendriers fusionnés avec clients",
    calendarComingSoon: "Vue calendrier bientôt disponible",
    upcomingMeetingsCount: "réunions à venir",
    documentsTitle: "Documents",
    documentsSubtitle: "Tous les documents liés aux mandats",
    noDocumentsYet: "Aucun document pour le moment",
    messagesTitle: "Messages",
    messagesSubtitle: "Communications avec les clients",
    noMessagesYet: "Aucun message pour le moment",
    myPartnerships: "Mes Partenariats",
    partnershipsSubtitle: "Relations et collaborations commerciales",
    noPartnershipsYet: "Aucun partenariat pour le moment",
    partnershipsHint: "Vous serez ajouté comme partenaire lorsque les clients vous sélectionneront pour leurs deals",
    activePartnershipsTitle: "Partenariats Actifs",
    specialty: "Spécialité",
    revenue: "Revenus",
    calendarIntegration: "Intégration Calendrier",
    message: "Message",
    pendingPartnerships: "Partenariats en Attente",
    loading: "Chargement du tableau de bord...",
  },
};

export default function ConsultantDashboard() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  // State
  const [activeTab, setActiveTab] = useState("overview");
  const [clients, setClients] = useState<Client[]>([]);
  const [mandates, setMandates] = useState<Mandate[]>([]);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  useEffect(() => {
    if (user) {
      loadConsultantDashboard();
    }
  }, [user]);

  const loadConsultantDashboard = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Load clients
      const clientsRef = collection(db, "consultantClients");
      const clientsQuery = query(
        clientsRef,
        where("consultantId", "==", user.email),
        orderBy("addedAt", "desc")
      );
      const clientsSnapshot = await getDocs(clientsQuery);
      const clientsData = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Client[];
      // Use mock data if Firestore is empty
      setClients(clientsData.length > 0 ? clientsData : mockClients);

      // Load mandates
      const mandatesRef = collection(db, "consultantMandates");
      const mandatesQuery = query(
        mandatesRef,
        where("consultantId", "==", user.email),
        orderBy("startDate", "desc")
      );
      const mandatesSnapshot = await getDocs(mandatesQuery);
      const mandatesData = mandatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Mandate[];
      // Use mock data if Firestore is empty
      setMandates(mandatesData.length > 0 ? mandatesData : mockMandates);

      // Load partnerships
      const partnershipsRef = collection(db, "consultantPartnerships");
      const partnershipsQuery = query(
        partnershipsRef,
        where("consultantId", "==", user.email)
      );
      const partnershipsSnapshot = await getDocs(partnershipsQuery);
      const partnershipsData = partnershipsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Partnership[];
      // Use mock data if Firestore is empty
      setPartnerships(partnershipsData.length > 0 ? partnershipsData : mockPartnerships);

      // Load meetings
      const meetingsRef = collection(db, "consultantMeetings");
      const meetingsQuery = query(
        meetingsRef,
        where("consultantId", "==", user.email),
        orderBy("scheduledAt", "desc")
      );
      const meetingsSnapshot = await getDocs(meetingsQuery);
      const meetingsData = meetingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Meeting[];
      // Use mock data if Firestore is empty
      setMeetings(meetingsData.length > 0 ? meetingsData : mockMeetings);

    } catch (error) {
      console.error("Error loading consultant dashboard:", error);
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createMergedCalendar = async (clientId: string) => {
    if (!user) return;

    try {
      const mergedCalendarRef = await addDoc(collection(db, "mergedCalendars"), {
        consultantId: user.email,
        clientId: clientId,
        syncEnabled: true,
        createdAt: serverTimestamp(),
        consultantAvailability: [],
        clientAvailability: []
      });

      const mergedCalendarUrl = `${window.location.origin}/calendar/merged/${mergedCalendarRef.id}`;

      await updateDoc(mergedCalendarRef, {
        mergedCalendarUrl
      });

      // Update client with calendar info
      const clientRef = doc(db, "consultantClients", clientId);
      await updateDoc(clientRef, {
        calendarId: mergedCalendarRef.id,
        mergedCalendarUrl
      });

      toast({
        title: language === 'fr' ? "Calendrier Fusionné" : "Calendar Merged",
        description: language === 'fr' ? "Calendrier fusionné créé avec succès" : "Merged calendar created successfully"
      });

      await loadConsultantDashboard();
      return mergedCalendarUrl;
    } catch (error) {
      console.error("Error creating merged calendar:", error);
      toast({
        title: "Error",
        description: language === 'fr' ? "Échec de la création du calendrier fusionné" : "Failed to create merged calendar",
        variant: "destructive"
      });
    }
  };

  // Calculate stats
  const stats = {
    totalClients: clients.length,
    activeClients: clients.filter(c => c.status === 'active').length,
    activeMandates: mandates.filter(m => m.status === 'in_progress').length,
    completedMandates: mandates.filter(m => m.status === 'completed').length,
    totalRevenue: mandates
      .filter(m => m.status === 'completed')
      .reduce((sum, m) => sum + m.totalFee, 0),
    pendingRevenue: mandates
      .filter(m => m.status === 'in_progress')
      .reduce((sum, m) => sum + (m.hourlyRate * m.hoursWorked), 0),
    totalHoursWorked: mandates.reduce((sum, m) => sum + m.hoursWorked, 0),
    upcomingMeetings: meetings.filter(m =>
      m.status === 'scheduled' &&
      m.scheduledAt?.toDate() > new Date()
    ).length,
    activePartnerships: partnerships.filter(p => p.status === 'active').length,
  };

  // Using formatCurrency from company.config for USD default

  const getMandateTypeLabel = (type: string) => {
    const labels = {
      'legal': language === 'fr' ? 'Services Juridiques' : 'Legal Services',
      'tax': language === 'fr' ? 'Conseil Fiscal' : 'Tax Advisory',
      'financial': language === 'fr' ? 'Analyse Financière' : 'Financial Analysis',
      'immigration': language === 'fr' ? 'Droit Immigration' : 'Immigration Law',
      'due_diligence': language === 'fr' ? 'Due Diligence' : 'Due Diligence',
      'other': language === 'fr' ? 'Autres Services' : 'Other Services'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
      case 'in_progress':
        return 'default';
      case 'completed':
        return 'secondary';
      case 'pending':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'destructive';
      case 'high':
        return 'default';
      case 'medium':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">{t.loading}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
            <p className="text-muted-foreground mt-2">{t.subtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate("/consultant/analytics")}>
              <TrendingUp className="mr-2 h-4 w-4" />
              {language === 'fr' ? 'Analytiques' : 'Analytics'}
            </Button>
            <Button onClick={() => navigate("/consultant/new-mandate")}>
              <Plus className="mr-2 h-4 w-4" />
              {t.newMandate}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.totalClients}</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalClients}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.activeMandates}</CardTitle>
              <Briefcase className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeMandates}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.completed}</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.completedMandates}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.hoursWorked}</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalHoursWorked}</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.partnerships}</CardTitle>
              <Handshake className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activePartnerships}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.totalRevenue}</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.pendingRevenue}</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(stats.pendingRevenue)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t.upcomingMeetings}</CardTitle>
              <Video className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.upcomingMeetings}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="overview">{t.overview}</TabsTrigger>
            <TabsTrigger value="clients">{t.myClients}</TabsTrigger>
            <TabsTrigger value="mandates">{t.mandates}</TabsTrigger>
            <TabsTrigger value="partnerships">{t.partnershipsTab}</TabsTrigger>
            <TabsTrigger value="calendar">{t.calendar}</TabsTrigger>
            <TabsTrigger value="documents">{t.documents}</TabsTrigger>
            <TabsTrigger value="messages">{t.messages}</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <OnboardingGuide
              role="consultant"
              language={language as 'en' | 'fr'}
            />
            <div className="grid gap-4 md:grid-cols-2">
              {/* Active Mandates */}
              <Card>
                <CardHeader>
                  <CardTitle>{t.activeMandatesTitle}</CardTitle>
                  <CardDescription>{t.currentlyInProgress}</CardDescription>
                </CardHeader>
                <CardContent>
                  {mandates.filter(m => m.status === 'in_progress').length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">{t.noActiveMandates}</p>
                  ) : (
                    <div className="space-y-4">
                      {mandates.filter(m => m.status === 'in_progress').slice(0, 3).map(mandate => (
                        <div key={mandate.id} className="border rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium">{mandate.title}</h4>
                            <Badge variant={getPriorityBadgeVariant(mandate.priority)}>
                              {mandate.priority}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{mandate.clientName}</p>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                              {t.dueDate}: {mandate.dueDate?.toDate().toLocaleDateString()}
                            </span>
                            <span>
                              {t.hours}: {mandate.hoursWorked}/{mandate.estimatedHours}h
                            </span>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full"
                            onClick={() => setActiveTab("mandates")}
                          >
                            {t.viewDetails}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Upcoming Meetings */}
              <Card>
                <CardHeader>
                  <CardTitle>{t.upcomingMeetings}</CardTitle>
                  <CardDescription>{t.nextScheduled}</CardDescription>
                </CardHeader>
                <CardContent>
                  {stats.upcomingMeetings === 0 ? (
                    <p className="text-muted-foreground text-center py-4">{t.noUpcomingMeetings}</p>
                  ) : (
                    <div className="space-y-4">
                      {meetings.filter(m => m.status === 'scheduled').slice(0, 5).map(meeting => (
                        <div key={meeting.id} className="flex items-center justify-between border-b pb-3">
                          <div>
                            <h4 className="font-medium">{meeting.title}</h4>
                            <p className="text-sm text-muted-foreground">{meeting.clientName}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline">{meeting.duration} min</Badge>
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {meeting.scheduledAt?.toDate().toLocaleString()}
                              </span>
                            </div>
                          </div>
                          <Button size="sm" onClick={() => window.open(meeting.meetingLink, '_blank')}>
                            {t.joinMeeting}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Compact Calendar Widget */}
            <CompactCalendarWidget />

            {/* Recent Partnerships */}
            <Card>
              <CardHeader>
                <CardTitle>{t.recentPartnerships}</CardTitle>
                <CardDescription>{t.activePartnerships}</CardDescription>
              </CardHeader>
              <CardContent>
                {partnerships.filter(p => p.status === 'active').length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">{t.noActivePartnerships}</p>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {partnerships.filter(p => p.status === 'active').slice(0, 3).map(partnership => (
                      <div key={partnership.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback>
                              {partnership.partnerName.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h4 className="font-medium">{partnership.partnerName}</h4>
                            <p className="text-sm text-muted-foreground">{partnership.partnerCompany}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{t.type}:</span>
                          <Badge variant="outline">{partnership.partnerType}</Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{t.sharedDeals}:</span>
                          <span>{partnership.sharedDeals.length}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{t.calendarLabel}:</span>
                          {partnership.calendarMerged ? (
                            <Badge variant="secondary" className="text-xs">
                              <CheckCircle className="mr-1 h-3 w-3" />
                              {t.merged}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">{t.notMerged}</Badge>
                          )}
                        </div>
                        {partnership.calendarMerged && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full"
                            onClick={() => window.open(partnership.mergedCalendarUrl, '_blank')}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {t.viewCalendar}
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <CardTitle>{t.clientsTitle}</CardTitle>
                    <CardDescription>{t.clientsSubtitle}</CardDescription>
                  </div>
                  <Button onClick={() => navigate("/consultant/add-client")}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    {t.addClient}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t.searchClients}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder={t.allStatus} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t.allStatus}</SelectItem>
                      <SelectItem value="active">{t.active}</SelectItem>
                      <SelectItem value="inactive">{t.inactive}</SelectItem>
                      <SelectItem value="completed">{t.completed}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {clients.length === 0 ? (
                  <div className="text-center py-10">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{t.noClientsYet}</p>
                    <Button className="mt-4" onClick={() => navigate("/consultant/add-client")}>
                      {t.addFirstClient}
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {clients
                      .filter(client =>
                        (filterStatus === 'all' || client.status === filterStatus) &&
                        (searchTerm === '' ||
                          client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          client.email.toLowerCase().includes(searchTerm.toLowerCase())
                        )
                      )
                      .map(client => (
                        <Card key={client.id} className="border">
                          <CardHeader className="pb-3">
                            <div className="flex items-center gap-3">
                              <Avatar>
                                <AvatarFallback>
                                  {client.name.substring(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <CardTitle className="text-lg">{client.name}</CardTitle>
                                <CardDescription>{client.company}</CardDescription>
                              </div>
                              <Badge variant={getStatusBadgeVariant(client.status)}>
                                {client.status}
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{t.type}</span>
                              <Badge variant="outline">{client.type}</Badge>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{t.activeMandates}</span>
                              <span>{client.activeMandates}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{t.totalPaid}</span>
                              <span>{formatCurrency(client.totalPaid)}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{t.calendarLabel}</span>
                              {client.calendarId ? (
                                <Badge variant="secondary" className="text-xs">
                                  <CheckCircle className="mr-1 h-3 w-3" />
                                  {t.merged}
                                </Badge>
                              ) : (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => createMergedCalendar(client.id)}
                                >
                                  <LinkIcon className="mr-1 h-3 w-3" />
                                  Merge
                                </Button>
                              )}
                            </div>
                            <div className="pt-2 border-t">
                              <p className="text-xs text-muted-foreground mb-1">{t.contact}</p>
                              <p className="text-sm">{client.email}</p>
                              <p className="text-sm">{client.phone}</p>
                            </div>
                            <div className="flex gap-2 pt-2">
                              {client.profileUrl && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => window.open(client.profileUrl, '_blank')}
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  {t.viewProfile}
                                </Button>
                              )}
                              {client.calendarId && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => navigate(`/calendar/merged/${client.calendarId}`)}
                                >
                                  <Calendar className="mr-2 h-4 w-4" />
                                  {t.calendar}
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => navigate(`/consultant/client/${client.id}`)}
                              >
                                <MessageSquare className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mandates Tab */}
          <TabsContent value="mandates" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t.mandatesTitle}</CardTitle>
                    <CardDescription>{t.allMandates}</CardDescription>
                  </div>
                  <Button onClick={() => navigate("/consultant/new-mandate")}>
                    <Plus className="mr-2 h-4 w-4" />
                    {t.newMandate}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {mandates.length === 0 ? (
                  <div className="text-center py-10">
                    <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{t.noMandatesYet}</p>
                    <Button className="mt-4" onClick={() => navigate("/consultant/new-mandate")}>
                      {t.createFirstMandate}
                    </Button>
                  </div>
                ) : (
                  <Tabs defaultValue="in_progress">
                    <TabsList>
                      <TabsTrigger value="in_progress">
                        {t.inProgress} ({mandates.filter(m => m.status === 'in_progress').length})
                      </TabsTrigger>
                      <TabsTrigger value="pending">
                        {t.pending} ({mandates.filter(m => m.status === 'pending').length})
                      </TabsTrigger>
                      <TabsTrigger value="completed">
                        {t.completed} ({mandates.filter(m => m.status === 'completed').length})
                      </TabsTrigger>
                    </TabsList>
                    {['in_progress', 'pending', 'completed'].map(status => (
                      <TabsContent key={status} value={status} className="space-y-4 mt-4">
                        {mandates
                          .filter(m => m.status === status)
                          .map(mandate => (
                            <Card key={mandate.id} className={status === 'in_progress' ? 'border-blue-200 bg-blue-50/50' : ''}>
                              <CardHeader>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <CardTitle className="text-lg">{mandate.title}</CardTitle>
                                    <CardDescription>
                                      {getMandateTypeLabel(mandate.type)}
                                    </CardDescription>
                                  </div>
                                  <div className="flex gap-2">
                                    <Badge variant={getStatusBadgeVariant(mandate.status)}>
                                      {mandate.status.replace('_', ' ')}
                                    </Badge>
                                    <Badge variant={getPriorityBadgeVariant(mandate.priority)}>
                                      {mandate.priority}
                                    </Badge>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent className="space-y-4">
                                <p className="text-sm text-muted-foreground">
                                  {t.client}: {mandate.clientName}
                                  {mandate.dealName && ` • Deal: ${mandate.dealName}`}
                                </p>
                                <p className="text-sm">{mandate.description}</p>

                                {mandate.status === 'in_progress' && (
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                      <span>{t.progress}</span>
                                      <span>{Math.round((mandate.hoursWorked / mandate.estimatedHours) * 100)}%</span>
                                    </div>
                                    <Progress value={(mandate.hoursWorked / mandate.estimatedHours) * 100} />
                                  </div>
                                )}

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                  <div>
                                    <span className="text-muted-foreground">{t.startDate}</span>
                                    <p>{mandate.startDate?.toDate().toLocaleDateString()}</p>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">{t.dueDate}</span>
                                    <p>{mandate.dueDate?.toDate().toLocaleDateString()}</p>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">{t.hours}</span>
                                    <p>{mandate.hoursWorked}/{mandate.estimatedHours}h</p>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">{t.rate}</span>
                                    <p>${mandate.hourlyRate}/h</p>
                                  </div>
                                </div>

                                {mandate.milestones && mandate.milestones.length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium mb-2">{t.milestones}</p>
                                    <div className="space-y-1">
                                      {mandate.milestones.map(milestone => (
                                        <div key={milestone.id} className="flex items-center gap-2 text-sm">
                                          <CheckCircle className={`h-4 w-4 ${milestone.completed ? 'text-green-500' : 'text-muted-foreground'}`} />
                                          <span>{milestone.title}</span>
                                          <span className="text-muted-foreground">
                                            {milestone.dueDate?.toDate().toLocaleDateString()}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {mandate.calendarIntegration && (
                                  <div className="flex items-center gap-2 text-sm text-blue-600">
                                    <Calendar className="h-4 w-4" />
                                    <span>{t.calendarMergedWithClient}</span>
                                    <Button 
                                      variant="link" 
                                      size="sm"
                                      onClick={() => window.open(mandate.calendarIntegration?.mergedCalendarUrl, '_blank')}
                                    >
                                      {t.viewCalendar}
                                    </Button>
                                  </div>
                                )}

                                <div className="flex items-center justify-between pt-4 border-t">
                                  <div>
                                    <span className="text-sm text-muted-foreground">{t.totalFee}: </span>
                                    <span className="font-bold">{formatCurrency(mandate.totalFee, mandate.currency)}</span>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => navigate(`/consultant/mandate/${mandate.id}`)}
                                    >
                                      <Eye className="mr-2 h-4 w-4" />
                                      {t.viewDetails}
                                    </Button>
                                    {mandate.documents && mandate.documents.length > 0 && (
                                      <Button variant="outline" size="sm">
                                        <FileText className="mr-2 h-4 w-4" />
                                        {t.documents} ({mandate.documents.length})
                                      </Button>
                                    )}
                                    {mandate.status === 'in_progress' && (
                                      <Button size="sm">
                                        {t.updateProgress}
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                      </TabsContent>
                    ))}
                  </Tabs>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partnerships Tab */}
          <TabsContent value="partnerships" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t.myPartnerships}</CardTitle>
                <CardDescription>{t.partnershipsSubtitle}</CardDescription>
              </CardHeader>
              <CardContent>
                {partnerships.length === 0 ? (
                  <div className="text-center py-10">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{t.noPartnershipsYet}</p>
                    <p className="text-sm text-muted-foreground mt-2">{t.partnershipsHint}</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold mb-4">{t.activePartnershipsTitle}</h3>
                      <div className="grid gap-4 md:grid-cols-2">
                        {partnerships
                          .filter(p => p.status === 'active')
                          .map(partnership => (
                            <Card key={partnership.id} className="border-green-200 bg-green-50/50">
                              <CardHeader>
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-12 w-12">
                                    <AvatarFallback>
                                      {partnership.partnerName.substring(0, 2).toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <CardTitle className="text-lg">{partnership.partnerName}</CardTitle>
                                    <CardDescription>{partnership.partnerCompany}</CardDescription>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-muted-foreground">{t.type}</span>
                                    <p><Badge variant="outline">{partnership.partnerType}</Badge></p>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">{t.specialty}</span>
                                    <p>{partnership.specialty || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">{t.sharedDeals}</span>
                                    <p>{partnership.sharedDeals.length}</p>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">{t.revenue}</span>
                                    <p>{formatCurrency(partnership.revenue)}</p>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-muted-foreground">{t.calendarIntegration}:</span>
                                  {partnership.calendarMerged ? (
                                    <Badge variant="secondary">
                                      <CheckCircle className="mr-1 h-3 w-3" />
                                      {t.active}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline">{t.notMerged}</Badge>
                                  )}
                                </div>
                                <div className="flex gap-2 pt-2">
                                  {partnership.calendarMerged && (
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => window.open(partnership.mergedCalendarUrl, '_blank')}
                                    >
                                      📅 {t.viewCalendar}
                                    </Button>
                                  )}
                                  <Button variant="outline" size="sm">
                                    💬 {t.message}
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                      </div>
                    </div>

                    {partnerships.filter(p => p.status === 'pending').length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-4">{t.pendingPartnerships}</h3>
                        <div className="grid gap-4 md:grid-cols-2">
                          {partnerships
                            .filter(p => p.status === 'pending')
                            .map(partnership => (
                              <Card key={partnership.id}>
                                <CardHeader>
                                  <div className="flex items-center gap-3">
                                    <Avatar>
                                      <AvatarFallback>
                                        {partnership.partnerName.substring(0, 2).toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1">
                                      <CardTitle className="text-lg">{partnership.partnerName}</CardTitle>
                                      <Badge variant="outline">{partnership.partnerType}</Badge>
                                    </div>
                                    <Badge variant="outline">{t.pending}</Badge>
                                  </div>
                                </CardHeader>
                              </Card>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-4">
            <div className="flex justify-end mb-4">
              <CalendarSyncButton 
                onSync={() => {
                  toast({ title: language === 'en' ? 'Calendar synced' : 'Calendrier synchronisé' });
                }} 
              />
            </div>
            <VisualCalendar 
              meetings={meetings.map(m => ({
                id: m.id,
                title: m.title,
                description: m.description,
                organizerId: user?.email || '',
                participants: m.attendees || [],
                date: m.scheduledAt?.toDate?.()?.toISOString?.() || new Date().toISOString(),
                time: m.scheduledAt?.toDate?.() 
                  ? `${m.scheduledAt.toDate().getHours().toString().padStart(2, '0')}:${m.scheduledAt.toDate().getMinutes().toString().padStart(2, '0')}`
                  : '09:00',
                duration: m.duration,
                status: m.status === 'scheduled' ? 'confirmed' : m.status === 'rescheduled' ? 'pending' : m.status as any,
                type: 'internal' as const,
                meetingUrl: m.meetingLink,
                createdAt: new Date(),
              }))}
              onNewMeeting={() => navigate("/consultant/schedule-meeting")}
              onMeetingClick={(meeting) => {
                if (meeting.meetingUrl) {
                  window.open(meeting.meetingUrl, '_blank');
                }
              }}
              onJoinMeeting={(meeting) => {
                if (meeting.meetingUrl) {
                  window.open(meeting.meetingUrl, '_blank');
                }
              }}
            />
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t.documentsTitle}</CardTitle>
                <CardDescription>{t.documentsSubtitle}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-10">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">{t.noDocumentsYet}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t.messagesTitle}</CardTitle>
                <CardDescription>{t.messagesSubtitle}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-10">
                  <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">{t.noMessagesYet}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
    </div>
  );
}
